import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, Trash2, Eye, EyeOff, Building2, Key, Shield, 
  Loader2, Edit2, ExternalLink, Users 
} from 'lucide-react';
import { useTumVendorCustomerCredentials, VendorCustomerCredential, VendorCustomerCredentialInsert } from '@/hooks/useTumVendorCustomerCredentials';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import type { TumUtilityVendor } from '@/types/tum';

interface VendorCustomerCredentialsModalProps {
  open: boolean;
  onClose: () => void;
  vendor: TumUtilityVendor;
}

interface SecurityQuestion {
  question: string;
  answer: string;
}

export const VendorCustomerCredentialsModal = ({
  open,
  onClose,
  vendor,
}: VendorCustomerCredentialsModalProps) => {
  const { currentWorkspace } = useWorkspace();
  const { 
    credentials, 
    isLoading, 
    createCredential, 
    updateCredential, 
    deleteCredential,
    isCreating,
    isUpdating,
    isDeleting,
  } = useTumVendorCustomerCredentials(vendor.id);

  // Fetch customers (onboarding projects)
  const { data: customers = [] } = useQuery({
    queryKey: ['onboarding-projects', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_onboarding_projects')
        .select('id, project_name, customer_name')
        .eq('workspace_id', currentWorkspace.id)
        .order('customer_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id && open,
  });

  // Form state
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form fields
  const [formData, setFormData] = useState<{
    customer_id: string;
    portal_url: string;
    portal_username: string;
    portal_password: string;
    notes: string;
    is_active: boolean;
    security_questions: SecurityQuestion[];
  }>({
    customer_id: '',
    portal_url: '',
    portal_username: '',
    portal_password: '',
    notes: '',
    is_active: true,
    security_questions: [],
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      customer_id: '',
      portal_url: vendor.portal_url || '',
      portal_username: '',
      portal_password: '',
      notes: '',
      is_active: true,
      security_questions: [],
    });
    setIsEditing(null);
    setShowAddForm(false);
  };

  // Load credential for editing
  const loadCredentialForEdit = (credential: VendorCustomerCredential) => {
    setFormData({
      customer_id: credential.customer_id || '',
      portal_url: credential.portal_url || '',
      portal_username: credential.portal_username || '',
      portal_password: credential.portal_password_encrypted || '',
      notes: credential.notes || '',
      is_active: credential.is_active,
      security_questions: credential.security_questions || [],
    });
    setIsEditing(credential.id);
    setShowAddForm(true);
  };

  // Handle save
  const handleSave = async () => {
    try {
      const payload: VendorCustomerCredentialInsert = {
        vendor_id: vendor.id,
        customer_id: formData.customer_id || null,
        portal_url: formData.portal_url || null,
        portal_username: formData.portal_username || null,
        portal_password_encrypted: formData.portal_password || null,
        notes: formData.notes || null,
        is_active: formData.is_active,
        security_questions: formData.security_questions,
      };

      if (isEditing) {
        await updateCredential({ id: isEditing, ...payload });
      } else {
        await createCredential(payload);
      }
      
      resetForm();
    } catch (error) {
      console.error('Failed to save credential:', error);
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      await deleteCredential(id);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete credential:', error);
    }
  };

  // Add security question
  const addSecurityQuestion = () => {
    setFormData(prev => ({
      ...prev,
      security_questions: [...prev.security_questions, { question: '', answer: '' }],
    }));
  };

  // Update security question
  const updateSecurityQuestion = (index: number, field: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      security_questions: prev.security_questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      ),
    }));
  };

  // Remove security question
  const removeSecurityQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      security_questions: prev.security_questions.filter((_, i) => i !== index),
    }));
  };

  // Get customers not yet assigned to this vendor
  const availableCustomers = customers.filter(
    c => !credentials.some(cred => cred.customer_id === c.id) || 
         (isEditing && credentials.find(cred => cred.id === isEditing)?.customer_id === c.id)
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Customer Credentials - {vendor.vendor_name}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="credentials" className="flex-1 min-h-0">
            <TabsList className="mb-2">
              <TabsTrigger value="credentials" className="text-xs">
                Credentials ({credentials.length})
              </TabsTrigger>
              <TabsTrigger value="add" className="text-xs" onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}>
                <Plus className="h-3 w-3 mr-1" />
                Add New
              </TabsTrigger>
            </TabsList>

            <TabsContent value="credentials" className="flex-1 min-h-0">
              <ScrollArea className="h-[400px] pr-2">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : credentials.length === 0 ? (
                  <Card className="p-6 text-center">
                    <Key className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">
                      No customer credentials configured for this vendor
                    </p>
                    <Button size="sm" onClick={() => setShowAddForm(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add First Credential
                    </Button>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {credentials.map(cred => (
                      <Card key={cred.id} className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {cred.customer_name || 'Default (All Customers)'}
                              </span>
                              <Badge 
                                variant={cred.is_active ? 'default' : 'secondary'} 
                                className="text-[10px]"
                              >
                                {cred.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                              {cred.portal_url && (
                                <div className="flex items-center gap-1">
                                  <ExternalLink className="h-3 w-3" />
                                  <a 
                                    href={cred.portal_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline truncate max-w-[200px]"
                                  >
                                    {cred.portal_url}
                                  </a>
                                </div>
                              )}
                              {cred.portal_username && (
                                <div className="flex items-center gap-1">
                                  <span className="text-muted-foreground">User:</span>
                                  <span className="font-mono">{cred.portal_username}</span>
                                </div>
                              )}
                              {cred.portal_password_encrypted && (
                                <div className="flex items-center gap-1">
                                  <span className="text-muted-foreground">Pass:</span>
                                  <span className="font-mono">
                                    {showPassword[cred.id] ? cred.portal_password_encrypted : '••••••••'}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => setShowPassword(prev => ({ 
                                      ...prev, 
                                      [cred.id]: !prev[cred.id] 
                                    }))}
                                  >
                                    {showPassword[cred.id] ? (
                                      <EyeOff className="h-3 w-3" />
                                    ) : (
                                      <Eye className="h-3 w-3" />
                                    )}
                                  </Button>
                                </div>
                              )}
                              {cred.security_questions?.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Shield className="h-3 w-3" />
                                  <span>{cred.security_questions.length} security question(s)</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => loadCredentialForEdit(cred)}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive"
                              onClick={() => setDeleteConfirmId(cred.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="add" className="flex-1 min-h-0">
              <ScrollArea className="h-[400px] pr-2">
                <div className="space-y-4 p-1">
                  {/* Customer Selection */}
                  <div className="space-y-1">
                    <Label className="text-xs">Customer</Label>
                    <Select
                      value={formData.customer_id}
                      onValueChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        customer_id: value === 'none' ? '' : value 
                      }))}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select customer (or leave blank for default)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Default (All Customers)</SelectItem>
                        {availableCustomers.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.customer_name || c.project_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      Leave blank to set default credentials for all customers
                    </p>
                  </div>

                  {/* Portal URL */}
                  <div className="space-y-1">
                    <Label className="text-xs">Portal URL</Label>
                    <Input
                      value={formData.portal_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, portal_url: e.target.value }))}
                      placeholder="https://portal.vendor.com"
                      className="h-8 text-sm"
                    />
                  </div>

                  {/* Credentials */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Username</Label>
                      <Input
                        value={formData.portal_username}
                        onChange={(e) => setFormData(prev => ({ ...prev, portal_username: e.target.value }))}
                        placeholder="Enter username"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword['form'] ? 'text' : 'password'}
                          value={formData.portal_password}
                          onChange={(e) => setFormData(prev => ({ ...prev, portal_password: e.target.value }))}
                          placeholder="Enter password"
                          className="h-8 text-sm pr-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-8 w-8 p-0"
                          onClick={() => setShowPassword(prev => ({ ...prev, form: !prev.form }))}
                        >
                          {showPassword['form'] ? (
                            <EyeOff className="h-3.5 w-3.5" />
                          ) : (
                            <Eye className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Security Questions */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Security Questions</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={addSecurityQuestion}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                    </div>
                    {formData.security_questions.map((q, idx) => (
                      <Card key={idx} className="p-2">
                        <div className="flex gap-2">
                          <div className="flex-1 space-y-1">
                            <Input
                              value={q.question}
                              onChange={(e) => updateSecurityQuestion(idx, 'question', e.target.value)}
                              placeholder="Security question"
                              className="h-7 text-xs"
                            />
                            <Input
                              value={q.answer}
                              onChange={(e) => updateSecurityQuestion(idx, 'answer', e.target.value)}
                              placeholder="Answer"
                              className="h-7 text-xs"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive shrink-0"
                            onClick={() => removeSecurityQuestion(idx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Notes */}
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes..."
                      className="text-sm min-h-[60px]"
                    />
                  </div>

                  {/* Active Toggle */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label className="text-xs">Active</Label>
                  </div>
                </div>
              </ScrollArea>

              <div className="flex justify-end gap-2 pt-3 border-t mt-3">
                <Button variant="outline" size="sm" onClick={resetForm}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSave}
                  disabled={isCreating || isUpdating}
                >
                  {(isCreating || isUpdating) && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                  {isEditing ? 'Update' : 'Save'} Credentials
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credentials</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete these customer credentials? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
